import * as React from "react";
import {Policy, Types, ICustomerPortalService, ProductsEnum, ValuableItem, FalconNavigatorHeader, POLICY_STATUS_MTC} from "@falcon/services";
import {authorize, IWithRouterProps, Overlay, withRouter} from "@athena-portal/components";
import {PluginTemplates} from "../../../../../utils/PluginTemplates";
import {lazyInject} from "@athena-portal/core";
import FalconHeader from "../../../../../components/navigation/FalconHeader";
import FalconFooter from "../../../../../components/navigation/FalconFooter";
import {InfoModal} from "../../../../../components/modals/InfoModal";
import {AddItemPanel} from "./AddItemPanel";
import ValuableItemPanel from "./ValuableItemPanel";
import {ValuablesSuccessModal} from "../../../../../components/modals/ValuablesSuccessModal";
import {
    calculateValuablesTotalValue,
    getAmountToDisplay,
    mergePolicyAndInteractionValuableItems
} from "../../../../../CommonFunctions";

interface IState {
    policies?: Policy[]
    valuableItems?: ValuableItem[]
}

@authorize
export class ValuablesPagePlain extends React.Component<IWithRouterProps, IState> {

    private pluginTemplates = new PluginTemplates();
    private readonly itemsCountLimit: number = 10;
    private readonly itemsTotalValueLimit: number = 20000;
    private cancelled: boolean = false;

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    constructor(props: IWithRouterProps) {
        super(props);
        this.state = {};
        this.pluginTemplates.setPageTitle("Your valuables");
    }

    public componentDidMount(): void {
        this.customerPortalService.getPolicies(ProductsEnum.RENTERS).subscribe((policies: Policy[]) => {
            const valuableItems: ValuableItem[] = mergePolicyAndInteractionValuableItems(policies);
            this.setState({policies, valuableItems});
        });
    }

    public render() {
        if (this.state.policies) {
            this.cancelled = this.state.policies.every(p => p.status === POLICY_STATUS_MTC);
        }
        if (this.state.valuableItems) {
            return (
                <>
                    <FalconHeader/>
                    <div className="valuables-page page-contents">
                        <FalconNavigatorHeader back="/dashboard/monthlySubscription" title="Specified items"/>
                        {this.renderHeadsUpPanel()}
                        {this.state.valuableItems!.length > 0 && this.renderPageContent()}
                        {this.state.valuableItems!.length < this.itemsCountLimit && calculateValuablesTotalValue(this.state.valuableItems!) < this.itemsTotalValueLimit && !this.cancelled && (
                            <AddItemPanel interactionId={this.state.policies![0].interaction._id}
                                          items={this.state.valuableItems}
                                          navigate={this.props.navigate}/>
                        )}
                        {this.pluginTemplates.renderBackLink("Back", false, "/dashboard/monthlySubscription")}
                    </div>
                    <FalconFooter/>
                    <ValuablesSuccessModal/>
                </>
            );
        } else {
            return <Overlay show={true}/>
        }
    }

    private renderHeadsUpPanel(): React.ReactNode {
        return (
            this.cancelled ?
                <div className="_panel banner banner-info">
                    Heads up - due to <b>cancellation</b>, adding / editing your items is not available.
                </div> :
                this.state.valuableItems!.length > 0 ?
                    <div className="_panel banner banner-info">
                        Heads up - <b>upload a photo or/and receipt of your items.</b> This will make the claim process easier.
                    </div> :
                    <div className="panel banner banner-info">
                        You don't have any specified items covered
                    </div>
        );
    }

    private renderPageContent(): React.ReactNode {
        return (
            <div className="_panel py-4">
                <div className="text-center">
                    <img src="/img/Flow-renters.svg" alt="Valuables"/>
                    <div className="fs-18 font-family-display font-weight-bold mt-2">Your specified items</div>
                </div>
                <div className="d-flex align-items-start my-3">
                    <div>
                        Cover for <b>10 items worth up to £5,000 each</b> (£20,000 in total) in any location
                    </div>
                    {this.renderInfoModal()}
                </div>
                <div className="mt-3">
                    {this.state.valuableItems!.length < this.itemsCountLimit
                        ? <>
                            You have added <b>{this.state.valuableItems!.length} </b>
                            of 10 items worth in total of
                            <b><span id="totalPrice">{getAmountToDisplay(calculateValuablesTotalValue(this.state.valuableItems!))}</span></b>
                        </>
                        : <>You have <b>{this.itemsCountLimit}</b> items added and you have reached your limit</>
                    }
                </div>
                {this.renderItems()}
            </div>
        );
    }

    private renderItems = (): React.ReactNode => {
        if (this.state.valuableItems!.length > 0) {
            const items: React.ReactNode[] = this.state.valuableItems!.map((item: ValuableItem) => {
                return <ValuableItemPanel key={item.valuableItemId}
                                          interaction={this.state.policies![0].interaction} item={item}
                                          cancelled={this.cancelled}
                                          {...this.props}/>;
            });
            return <>{...items}</>;
        } else {
            return <></>
        }
    };

    private renderInfoModal = (): React.ReactNode => {
        return (
            <InfoModal className="ml-3" title="Specified items">
                <p>
                    Think about items like phones, laptops, cameras, bikes or jewellery.  You can add up to <b>10 items with a maximum limit of £5,000 for any one item up to a maximum of £20,000 in total.</b>
                </p>
                <p>
                    <b>You can choose where you want the item covered – inside the home only or inside and outside of the home. You don’t need to specify items worth £2,000 or less unless you need cover outside of the home. </b>
                    And good news, you’ll be covered for accidental loss, damage and theft if you choose to cover the items outside of the home.
                </p>
                <p>
                    You can also add any <b>items worth more than £2,000 that stay in the home e.g. TVs or artwork. </b>
                    If you need accidental damage cover for these items,
                    <b> you’ll need to include optional accidental damage cover under contents. You don’t need to specify furniture items, </b>
                    as these are covered within your overall contents limit of cover.
                </p>
                <p>
                    <b>Your specified items are included in your total contents limit of cover, so please check you have enough cover.</b>
                </p>
            </InfoModal>
        )
    };
}


export const ValuablesPage = withRouter(ValuablesPagePlain)
